import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';

import { SidebarNavMenuItem } from '../../../../decorators/sidebar-nav-menu-item';
import { ClassesEntity } from '../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../injection-tokens/entity-service-tokens';
import { ErrorHandlerService } from '../../../../decoration-base/error-handler.service';
import { Log } from '../../../../services/logger.service';
import { UserPrefsService } from '../../../../services/user-prefs.service';
import { AuthService } from '../../../../services/auth.service';
import { isParticipant } from '../../../../utils/auth.utils';
import { ClassEntityService } from '../../../../services/entity-services/classes-entity.service';
import { ConfirmDialogService } from '../../../../services/confirm-dialog.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../shared/pagination-options';

type ViewMode = 'board' | 'table';
export type LaneKey = 'pending' | 'upcoming' | 'ongoing' | 'completed';

/** Number of cards shown per lane before the "Show more" button appears. */
const LANE_PAGE_SIZE = 10;

/** Column definitions for the flat table view. */
const TABLE_COLUMNS: string[] = ['name', 'course', 'trainers', 'startDate', 'endDate', 'status', 'stage', 'actions'];

/** UserPrefs page key for the classes info banner. */
const BANNER_PAGE = 'classes';

@SidebarNavMenuItem({
  route: '/classes',
  faIcon: 'co_present',
  name: 'Classes',
  order: 200,
  businessProcessesName: 'Classes',
})
@Component({
  selector: 'app-classes-v2',
  templateUrl: './classes-v2.component.html',
  styleUrls: ['./classes-v2.component.css'],
})
export class ClassesV2Component implements OnInit {
  private readonly logger = Log.create(ClassesV2Component);

  // ── View state ─────────────────────────────────────────────────────
  isLoading = true;
  viewMode: ViewMode = 'board';
  searchQuery = '';

  // ── Banner state ──────────────────────────────────────────────────────────
  bannerVisible = false;
  bannerDismissForever = false;

  // ── Lane arrays (full filtered lists) ──────────────────────────────
  pendingClasses: ClassesEntity[] = [];
  upcomingClasses: ClassesEntity[] = [];
  ongoingClasses: ClassesEntity[] = [];
  completedClasses: ClassesEntity[] = [];

  // ── Visible slices (load-more window) ──────────────────────────────
  visiblePending: ClassesEntity[] = [];
  visibleUpcoming: ClassesEntity[] = [];
  visibleOngoing: ClassesEntity[] = [];
  visibleCompleted: ClassesEntity[] = [];

  // ── Load-more flags ─────────────────────────────────────────────────
  hasPendingMore = false;
  hasUpcomingMore = false;
  hasOngoingMore = false;
  hasCompletedMore = false;

  // ── Table ───────────────────────────────────────────────────────────
  /** `true` when the current user is a management-team admin and may archive entities. */
  get canArchive(): boolean { return this.authService.canArchive(); }
  /** Active table columns; 'actions' is included only for management-team admins. */
  get tableColumns(): string[] {
    return this.canArchive ? TABLE_COLUMNS : TABLE_COLUMNS.filter(c => c !== 'actions');
  }
  tableDataSource = new MatTableDataSource<ClassesEntity>([]);
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  @ViewChild(MatSort) set matSortSetter(s: MatSort) {
    this.tableDataSource.sort = s ?? null;
  }
  @ViewChild(MatPaginator) set matPaginatorSetter(p: MatPaginator) {
    this.tableDataSource.paginator = p ?? null;
  }

  // ── Per-lane sort direction ─────────────────────────────────────────
  sortDir: Record<LaneKey, 'asc' | 'desc'> = {
    pending: 'desc',
    upcoming: 'desc',
    ongoing: 'desc',
    completed: 'desc',
  };

  toggleLaneSort(lane: LaneKey): void {
    this.sortDir[lane] = this.sortDir[lane] === 'desc' ? 'asc' : 'desc';
    this.recompute();
  }

  // ── Private ─────────────────────────────────────────────────────────
  private allClasses: ClassesEntity[] = [];
  private readonly userId = localStorage.getItem('userId') ?? '';

  private visibleCounts: Record<LaneKey, number> = {
    pending: LANE_PAGE_SIZE,
    upcoming: LANE_PAGE_SIZE,
    ongoing: LANE_PAGE_SIZE,
    completed: LANE_PAGE_SIZE,
  };

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
    private readonly userPrefs: UserPrefsService,
    private readonly authService: AuthService,
    private readonly classEntityService: ClassEntityService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const savedMode = this.userPrefs.get<ViewMode>('classes', 'view', 'mode');
    if (savedMode) this.viewMode = savedMode;

    // Show banner only for operator users who have not permanently dismissed it.
    this.bannerVisible =
      !isParticipant(this.authService.getCurrentUserValue()) &&
      !this.userPrefs.isBannerDismissed(BANNER_PAGE);

    this.classService.classGetByUserId(this.userId).subscribe({
      next: (res: any) => {
        this.allClasses = res.classes ?? [];
        this.logger.i('Classes loaded:', this.allClasses.length);
        this.isLoading = false;
        this.recompute();
      },
      error: (err: any) => {
        this.isLoading = false;
        this.errorHandler.handleError(err, 'Failed to load classes');
      },
    });

    this.tableDataSource.sortingDataAccessor = (item, column) => {
      const accessors: Record<string, (c: ClassesEntity) => string | number> = {
        name: c => c.name ?? '',
        course: c => this.getCourseName(c),
        startDate: c => this.getStartDate(c)?.getTime() ?? 0,
        endDate: c => this.getEndDate(c)?.getTime() ?? 0,
        status: c => this.getStatusLabel(c),
        stage: c => (c as any).stage ?? '',
      };
      return accessors[column]?.(item) ?? '';
    };
  }

  // ── Banner ─────────────────────────────────────────────────────────
  /** Hides the info banner; if "Don't show again" is checked, persists the dismissal. */
  dismissBanner(): void {
    this.bannerVisible = false;
    if (this.bannerDismissForever) {
      this.userPrefs.dismissBanner(BANNER_PAGE);
    }
  }

  // ── Search ──────────────────────────────────────────────────────────
  onSearchChange(): void {
    this.resetVisibleCounts();
    this.recompute();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onSearchChange();
  }

  // ── View mode ───────────────────────────────────────────────────────
  onViewModeChange(mode: ViewMode): void {
    this.viewMode = mode;
    this.userPrefs.set('classes', 'view', 'mode', mode);
  }

  // ── Load-more ───────────────────────────────────────────────────────
  loadMore(lane: LaneKey): void {
    this.visibleCounts[lane] += LANE_PAGE_SIZE;
    this.updateSlices();
  }

  // ── Navigation ──────────────────────────────────────────────────────
  navigateToClass(classId: string): void {
    this.router.navigate(['/class', classId]);
  }

  // ── Track-by for *ngFor performance ─────────────────────────────────
  trackById(_: number, cls: ClassesEntity): string {
    return cls.id;
  }

  // ── Table helpers ────────────────────────────────────────────────────
  getCourseName(cls: ClassesEntity): string {
    return (cls as any).trainingSchedule?.course?.name ?? 'Custom Class';
  }

  getTrainerNames(cls: ClassesEntity): string {
    const trainers = (cls as any).trainingSchedule?.trainers ?? [];
    if (!trainers.length) return '—';
    return trainers.map((t: any) => t.name ?? t).join(', ');
  }

  /** Table date display — reads from dateRanges[0] matching the raw API shape. */
  getStartDate(cls: ClassesEntity): Date | undefined {
    const d = (cls as any).trainingSchedule?.dateRanges?.[0]?.startDate;
    return d ? new Date(d) : undefined;
  }

  getEndDate(cls: ClassesEntity): Date | undefined {
    const d = (cls as any).trainingSchedule?.dateRanges?.[0]?.endDate;
    return d ? new Date(d) : undefined;
  }

  formatDate(d: Date | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getStage(cls: ClassesEntity): string {
    return (cls as any).stage ?? '—';
  }

  getStatusLabel(cls: ClassesEntity): string {
    const stage = (cls as any).stage as string | undefined;
    if (!stage || stage.toLowerCase() !== 'confirm') return 'Pending';
    const labels: Record<string, string> = { OnGoing: 'Ongoing', Upcoming: 'Upcoming', Completed: 'Completed' };
    return labels[cls.status ?? ''] ?? 'Pending';
  }

  getStatusCssClass(cls: ClassesEntity): string {
    const cssMap: Record<string, string> = {
      Ongoing: 'chip-ongoing',
      Upcoming: 'chip-upcoming',
      Completed: 'chip-completed',
    };
    return cssMap[this.getStatusLabel(cls)] ?? 'chip-pending';
  }

  // ── Private helpers ──────────────────────────────────────────────────
  private isConfirmed(cls: ClassesEntity): boolean {
    return ((cls as any).stage as string | undefined)?.toLowerCase() === 'confirm';
  }

  private bucket(lane: LaneKey): ClassesEntity[] {
    const buckets: Record<LaneKey, () => ClassesEntity[]> = {
      pending: () => this.allClasses.filter(c => !this.isConfirmed(c)),
      upcoming: () => this.allClasses.filter(c => this.isConfirmed(c) && c.status === 'Upcoming'),
      ongoing: () => this.allClasses.filter(c => this.isConfirmed(c) && c.status === 'OnGoing'),
      completed: () => this.allClasses.filter(c => this.isConfirmed(c) && c.status === 'Completed'),
    };
    return buckets[lane]();
  }

  private applyFilter(items: ClassesEntity[]): ClassesEntity[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(cls => this.matchesQuery(cls, q));
  }

  private matchesQuery(cls: ClassesEntity, q: string): boolean {
    const candidates = [
      cls.name,
      cls.trainingSchedule?.title,
      cls.trainingSchedule?.course?.name,
      ...(cls.trainingSchedule?.trainers?.map(t => t.name) ?? []),
      cls.startDate ? new Date(cls.startDate).toLocaleDateString('en-GB') : '',
      cls.endDate ? new Date(cls.endDate).toLocaleDateString('en-GB') : '',
    ];
    return candidates.some(f => f?.toLowerCase().includes(q));
  }

  private sortLane(items: ClassesEntity[], lane: LaneKey): ClassesEntity[] {
    const dir = this.sortDir[lane] === 'desc' ? -1 : 1;
    return [...items].sort((a, b) => {
      const ts = (c: ClassesEntity): number => {
        const d =
          (c as any).trainingSchedule?.dateRanges?.[0]?.startDate ??
          (c as any).createdOn;
        return d ? new Date(d).getTime() : 0;
      };
      const diff = ts(a) - ts(b);
      if (diff !== 0) return diff * dir;
      return ((a.name ?? '').localeCompare(b.name ?? '')) * dir;
    });
  }

  private recompute(): void {
    this.pendingClasses = this.sortLane(this.applyFilter(this.bucket('pending')), 'pending');
    this.upcomingClasses = this.sortLane(this.applyFilter(this.bucket('upcoming')), 'upcoming');
    this.ongoingClasses = this.sortLane(this.applyFilter(this.bucket('ongoing')), 'ongoing');
    this.completedClasses = this.sortLane(this.applyFilter(this.bucket('completed')), 'completed');
    this.updateSlices();
    this.refreshTableSource();
  }

  private updateSlices(): void {
    this.visiblePending = this.pendingClasses.slice(0, this.visibleCounts.pending);
    this.visibleUpcoming = this.upcomingClasses.slice(0, this.visibleCounts.upcoming);
    this.visibleOngoing = this.ongoingClasses.slice(0, this.visibleCounts.ongoing);
    this.visibleCompleted = this.completedClasses.slice(0, this.visibleCounts.completed);

    this.hasPendingMore = this.pendingClasses.length > this.visibleCounts.pending;
    this.hasUpcomingMore = this.upcomingClasses.length > this.visibleCounts.upcoming;
    this.hasOngoingMore = this.ongoingClasses.length > this.visibleCounts.ongoing;
    this.hasCompletedMore = this.completedClasses.length > this.visibleCounts.completed;
  }

  private resetVisibleCounts(): void {
    this.visibleCounts = {
      pending: LANE_PAGE_SIZE,
      upcoming: LANE_PAGE_SIZE,
      ongoing: LANE_PAGE_SIZE,
      completed: LANE_PAGE_SIZE,
    };
  }

  private refreshTableSource(): void {
    const q = this.searchQuery.trim().toLowerCase();
    this.tableDataSource.data = q
      ? this.allClasses.filter(c => this.matchesQuery(c, q))
      : [...this.allClasses];
  }

  /**
   * Prompts the user then archives the class via the API.
   * Reloads classes on success, shows a user-friendly error on failure.
   */
  archiveClass(classId: string): void {
    const cls = this.allClasses.find(c => c.id === classId);
    const name = cls?.name ?? 'this class';

    this.confirmDialog.confirmAction(
      'Archive Class',
      `Archive "${name}"? Archived classes are hidden from the board and table.`,
      'warning',
      'Archive',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.classEntityService.archiveClass(classId).subscribe({
        next: () => {
          this.snackBar.open('Class archived.', 'Dismiss', { duration: 3000 });
          this.allClasses = this.allClasses.filter(c => c.id !== classId);
          this.recompute();
        },
        error: () => {
          this.snackBar.open('Failed to archive class.', 'Dismiss', { duration: 4000 });
        }
      });
    });
  }
}
